import { Button, Form, Input, message, Modal, ModalProps } from 'antd';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { useTranslate } from '@tolgee/react';
import FormField from 'components/FormField';
import PlatformRegionSelect from './PlatformRegionSelect';
import PlatformSelect from './PlatformSelect';
import RegionSelect from './RegionSelect';
import ScopeSelect from './ScopeSelect';
import { PlatformRegions, SensitiveWordItem } from '../types';
import { ReactNode } from 'react';
import { addSensitiveWord, editSensitiveWord } from '../api';
import { get, uniq } from 'lodash-es';

export type FV = Partial<SensitiveWordItem>;

interface SensitiveWordFormProps extends FormikConfig<FV> {
  children: (
    nodes: Record<'word' | 'scopeType' | 'scope', ReactNode>,
    props: FormikProps<FV>,
  ) => JSX.Element;
}

export const defaultInitialValues: FV = {
  word: '',
  scopeType: 'ALL_PLATFORMS',
  platforms: [],
  regions: [],
  platformRegions: {},
};

const joinPlatformRegions = (platformRegionsList: FV['platformRegions'][]) => {
  return platformRegionsList.reduce<Exclude<FV['platformRegions'], undefined>>((res, cur) => {
    if (!cur) {
      return res;
    }
    return Object.keys(cur).reduce((res, key) => {
      res[key] = uniq([...(cur[key] || []), ...(res[key] || [])]);
      return res;
    }, res);
  }, {});
};

export default function SensitiveWordForm({ children, ...otherProps }: SensitiveWordFormProps) {
  const { t } = useTranslate();

  const validateWord = (value: string) => {
    if (!value || !value.trim()) {
      return t('department.请输入x', { x: t('敏感词内容') });
    }
    return undefined;
  };

  const validatePlatforms = (value: General.PlatformUpperCase[] | undefined) => {
    if (!value || value.length === 0) {
      return t('请选择');
    }
    return undefined;
  };

  const validateRegions = (value: General.UpperCaseRegion[] | undefined) => {
    if (!value || value.length === 0) {
      return t('请选择');
    }
    return undefined;
  };

  const validatePlatformRegions = (value: FV['platformRegions'] | undefined) => {
    if (!value || Object.keys(value).length === 0) {
      return t('请选择');
    }
    return undefined;
  };

  return (
    <Formik enableReinitialize {...otherProps}>
      {(props: FormikProps<FV>) => {
        const { values, setValues } = props;
        const scopeType = values.scopeType;

        const nodes = {
          word: (
            <FormField<string> name="word" label={t('敏感词内容')} required validate={validateWord}>
              {({ field }) => (
                <Input
                  placeholder={t('department.请输入x', { x: t('敏感词内容') })}
                  showCount
                  maxLength={50}
                  {...field}
                />
              )}
            </FormField>
          ),
          scopeType: (
            <FormField<FV['scopeType']> name="scopeType" label={t('适用范围')} required>
              {({ field }) => (
                <ScopeSelect
                  value={field.value}
                  onChange={(value) => {
                    setValues({
                      ...values,
                      scopeType: value,
                      platforms: [],
                      regions: [],
                      platformRegions: undefined,
                    });
                  }}
                />
              )}
            </FormField>
          ),
          scope: [
            scopeType === 'SPECIFIED_PLATFORMS' && (
              <FormField<FV['platforms']>
                name="platforms"
                label={t('指定平台')}
                required
                validate={validatePlatforms}
              >
                {({ field }) => <PlatformSelect {...field} />}
              </FormField>
            ),
            scopeType === 'SPECIFIED_REGIONS' && (
              <FormField<FV['regions']>
                name="regions"
                label={t('指定站点')}
                required
                validate={validateRegions}
              >
                {({ field }) => <RegionSelect {...field} />}
              </FormField>
            ),
            scopeType === 'SPECIFIED_PLATFORMS_REGIONS' && (
              <FormField<FV['platformRegions']>
                name="platformRegions"
                label={t('指定平台&站点')}
                required
                validate={validatePlatformRegions}
              >
                {({ field }) => <PlatformRegionSelect {...field} />}
              </FormField>
            ),
          ],
        };

        return children(nodes, props);
      }}
    </Formik>
  );
}

export function AddForm({
  onCheckWork,
  onUpdate,
}: {
  onCheckWork: (word: string) => void;
  onUpdate: () => void;
}) {
  const { t } = useTranslate();

  return (
    <SensitiveWordForm
      initialValues={defaultInitialValues}
      onSubmit={(values, helpers) => {
        const onSuccess = () => {
          message.success(t('phrase.保存成功'));
          helpers.resetForm();
          onUpdate();
        };
        return addSensitiveWord(values)
          .then(onSuccess)
          .catch((err) => {
            const { code, data } = get(err, 'response.data') || {};
            if (code !== 400 || !data) {
              message.error(t('phrase.保存失败，请稍后再试'));
              return;
            }
            const { conflictType, existingWord } = data;
            if (conflictType === 'DUPLICATE') {
              message.warn(t('已有相同敏感词'));
              return;
            }
            if (conflictType === 'CONFLICT' && existingWord) {
              return new Promise<void>((resolve, reject) => {
                Modal.confirm({
                  title: t('已有相同内容但适用范围不同的敏感词，请选择去查看或继续添加。'),
                  cancelText: t('去查看'),
                  okText: t('继续添加'),
                  onCancel: () => {
                    onCheckWork(existingWord.word);
                    resolve();
                  },
                  onOk: () => {
                    if (values.scopeType !== existingWord.scopeType) {
                      return reject();
                    }
                    return editSensitiveWord({
                      ...existingWord,
                      platforms:
                        values.scopeType === 'SPECIFIED_PLATFORMS'
                          ? uniq([...(values.platforms || []), ...(existingWord.platforms || [])])
                          : [],
                      regions:
                        values.scopeType === 'SPECIFIED_REGIONS'
                          ? uniq([...(values.regions || []), ...(existingWord.regions || [])])
                          : [],
                      platformRegions:
                        values.scopeType === 'SPECIFIED_PLATFORMS_REGIONS'
                          ? joinPlatformRegions([
                              values.platformRegions,
                              Object.keys(
                                existingWord.platformRegions as PlatformRegions,
                              ).reduceRight((res, key) => {
                                res[key] = (res[key] as any).regions;
                                return res;
                              }, existingWord.platformRegions as PlatformRegions),
                            ])
                          : undefined,
                    })
                      .then(onSuccess)
                      .catch(() => {
                        message.error(t('phrase.保存失败，请稍后再试'));
                      });
                  },
                });
              });
            }
          });
      }}
    >
      {({ word, scope, scopeType }, { values, handleSubmit, isSubmitting }) => {
        return (
          <Form layout="vertical">
            <div className="flex items-center gap-[16px]">
              <div style={{ flex: '2 0 0' }}>{word}</div>
              <div style={{ flex: '1 0 0' }}>{scopeType}</div>
              {values.scopeType !== 'ALL_PLATFORMS' && <div style={{ flex: '1 0 0' }}>{scope}</div>}
              <Button
                style={{ flex: '0 0 auto', marginTop: '5px' }}
                type="primary"
                loading={isSubmitting}
                onClick={() => handleSubmit()}
              >
                {t('添加')}
              </Button>
            </div>
          </Form>
        );
      }}
    </SensitiveWordForm>
  );
}

export function EditModal({
  sensitiveWordItem,
  onUpdate,
  ...otherProps
}: ModalProps & {
  sensitiveWordItem: SensitiveWordItem;
  onUpdate: () => void;
}) {
  const { t } = useTranslate();

  return (
    <SensitiveWordForm
      initialValues={sensitiveWordItem}
      onSubmit={(values, helpers) => {
        return editSensitiveWord(values as any)
          .then(() => {
            message.success(t('phrase.保存成功'));
            helpers.resetForm();
            otherProps.onCancel?.({} as any);
            onUpdate();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {({ word, scopeType, scope }, { handleSubmit, isSubmitting }) => {
        return (
          <Modal
            width={600}
            title="编辑敏感词"
            okText="确定"
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            {...otherProps}
          >
            <Form>
              {word}
              {scopeType}
              {scope}
            </Form>
          </Modal>
        );
      }}
    </SensitiveWordForm>
  );
}
